
import React from "react";
import LogoBar from "./home/LogoBar";
import FanArena from "./home/FanArena";
import SocialBand from "./home/SocialBand";
import NewsMatches from "./home/NewsMatches";
import HeroSlideshow from "./home/HeroSlideshow";
import SquadCarousel from "./home/SquadCarousel";
import PartnersSection from "./home/PartnersSection";
import CollectionSlider from "./home/CollectionSlider";
import { isPageVisible } from "../helpers";
import MemberModal from "./common/MemberModal";
import { BaseService, IGallery, ITeamMember, ITeamNews, ITeamWebsite, ITournamentEditionMatch, OmneHostService, SanitizedResponse } from "../../../../services";

export interface HomeProps {
    basePath: string;
    publicId: string;
    website: ITeamWebsite;
}

function Home({ website, basePath, publicId }: HomeProps) {

    const settings = website.home_settings ?? {} as ITeamWebsite["home_settings"];
    const [news, setNews] = React.useState<ITeamNews[]>();
    const [gallery, setGallery] = React.useState<IGallery[]>();
    const [members, setMembers] = React.useState<ITeamMember[]>();
    const [matches, setMatches] = React.useState<ITournamentEditionMatch[]>();
    const [selectedMember, setSelectedMember] = React.useState<ITeamMember | null>(null);
    const showSchedule = isPageVisible(website, "SCHEDULE") && settings.show_schedules !== false;
    const showGallery = isPageVisible(website, "GALLERY") && settings.show_top_gallery !== false;
    const showSponsors = !!settings.show_sponsors && !!website.sponsors?.length;

    React.useEffect(fetchInformation, [website.team.public_id]);

    return (<>
        <HeroSlideshow website={website} basePath={basePath} news={news} />
        {showSponsors && (<LogoBar logos={website.sponsors} />)}
        {isPageVisible(website, "MERCH") && (<CollectionSlider website={website} basePath={basePath} />)}
        <NewsMatches website={website} basePath={basePath} publicId={publicId} news={news} matches={showSchedule ? matches : undefined} />
        {(isPageVisible(website, "MEMBERS") && !!members?.length) && (<SquadCarousel website={website} members={members} onSelect={setSelectedMember} />)}
        {(showGallery && !!gallery?.length) && (<FanArena basePath={basePath} gallery={gallery} />)}
        <SocialBand website={website} />
        {showSponsors && (<PartnersSection website={website} />)}
        {selectedMember && (<MemberModal member={selectedMember} onClose={() => setSelectedMember(null)} />)}
    </>);

    function fetchInformation() {
        const omnehostService = OmneHostService.getInstance();
        const teamId = website.team.public_id;
        omnehostService.queryTeamNews(teamId, {
            size: 5,
            sort: "updated_at,DESC",
        }).then(({ content }: SanitizedResponse<ITeamNews>) => {
            setNews(content ?? []);
        }).catch(BaseService.reportError);
        if (showSchedule) {
            omnehostService.queryTeamSchedule(teamId, {
                size: 20,
                sort: ["start_date,ASC", "status,ASC"],
            }).then(({ content }: SanitizedResponse<ITournamentEditionMatch>) => {
                setMatches(content ?? []);
            }).catch(BaseService.reportError);
        }
        if (isPageVisible(website, "MEMBERS")) {
            omnehostService.queryTeamMembers(teamId, {
                size: 12,
                status: "ACTIVE",
                sort: "role,DESC",
            }).then(({ content }: SanitizedResponse<ITeamMember>) => {
                setMembers(content ?? []);
            }).catch(BaseService.reportError);
        }
        if (showGallery) {
            omnehostService.queryTeamGallery(teamId, {
                size: 5,
                sort: "updated_at,DESC",
            }).then(({ content }: SanitizedResponse<IGallery>) => {
                setGallery(content ?? []);
            }).catch(BaseService.reportError);
        }
    }
}

export default Home;
